import { Navigate } from 'react-router-dom';
import { useParams } from "react-router";
import { usePortalVersion } from '../contexts/PortalVersionContext.jsx';
import Areas from "./Areas.jsx";
import Residents from "./Residents.jsx";
import ResidentsPage from "../pages/community/residents/ResidentsPage.jsx";
import LocatingPage from "../pages/community/residents/locating/LocatingPage.jsx";
import AreasPage from "../pages/community/areas/AreasPage.jsx";
import AreaProfilePage from "../pages/community/areas/area-profile/AreaProfilePage.jsx";
import Devices from "./Devices.jsx";
import DevicesPage from "../pages/devices/DevicesPage.jsx";
import AreaGroups from "./AreaGroups.jsx";
import SystemDevices from "./SystemDevices.jsx";
import ServerDevices from "./ServerDevices.jsx";
import ServerProfile from "./profile-templates/Server.jsx";
import ServerProfilePage from "../pages/servers/profile/ServerProfilePage.jsx";
import InovonicsDevice from "./profile-templates/InovonicsDevice.jsx";
import PalcareDevice from "./profile-templates/PalcareDevice.jsx";
import AccutechDevice from "./profile-templates/AccutechDevice.jsx";
import DeviceProfilePage from "../pages/devices/device-profile/DeviceProfilePage.jsx";
import WanderingProfilePage from "../pages/devices/wandering-profile/WanderingProfilePage.jsx";
import AreaProfile from "./profile-templates/Area.jsx";
import ResidentProfile from "./profile-templates/Resident.jsx";
import ResidentProfilePage from "../pages/community/residents/resident-profile/ResidentProfilePage.jsx";
import {iAmHigherAuthority} from "../libs.jsx";
import ResidentGroups from "./ResidentGroups.jsx";
import SessionReload from "./component-assets/SessionReload.jsx";
import WanderDevices from "./WanderDevices.jsx";
import { getSession, getAllVendors } from "../services/session";

const Community = (props) => {
    let pageContent, dData, paramVars, bData;
    paramVars = useParams();
    const { portalVersion } = usePortalVersion();
    const isV2 = portalVersion === 'v2';

    dData = getSession();
    if (paramVars.dType === "areas"){
        if (paramVars?.eType === "groups"){
            pageContent = isV2 ? <AreasPage initialTab="areagroups" /> : <AreaGroups />;
        }else if (paramVars?.eType === "buildings"){
            pageContent = isV2 ? <AreasPage initialTab="buildings" /> : <Areas />;
        }else if (paramVars?.eType && !isNaN(paramVars.eType) && iAmHigherAuthority(10, dData?.role_weight)){
            // Area profile: /community/areas/:areaId
            pageContent = isV2
                ? <AreaProfilePage id={paramVars.eType} />
                : <AreaProfile id={paramVars.eType} />;
        }else{
            pageContent = isV2 ? <AreasPage /> : <Areas />;
        }
    }else if (paramVars.dType === "residents"){
        if (paramVars?.eType === "groups"){
            // V2: render groups tab inside ResidentsPage; V1: standalone ResidentGroups
            pageContent = isV2 ? <ResidentsPage initialTab="groups" /> : <ResidentGroups />;
        }else if (paramVars?.eType === "locating" && isV2){
            // RTLS — only available on campuses with an active vendor 14 (the
            // Inovonics RTLS provider). Pulled from the full vendor list rather
            // than a type filter since the vendor's type classification may change.
            if (getAllVendors().some((v) => v.vendor_active && v.vendor_id === 14)) {
                pageContent = <LocatingPage />;
            } else {
                pageContent = <div>404 Page not found</div>;
            }
        }else if (paramVars?.eType && !isNaN(paramVars.eType) && iAmHigherAuthority(10, dData?.role_weight)){
            // Resident profile: /community/residents/:residentId
            pageContent = isV2
                ? <ResidentProfilePage id={paramVars.eType} />
                : <ResidentProfile id={paramVars.eType} />;
        }else{
            // V2: new residents page; V1: old residents component
            pageContent = isV2 ? <ResidentsPage /> : <Residents />;
        }
    }else if (paramVars.dType === "devices"){
        if (paramVars?.eType && paramVars?.kType) {
            // Device profile: /community/devices/:mfgId/:deviceId
            // mfgId=6 routes to the wandering (Accutech) profile; others go
            // through DeviceProfilePage (Inovonics mfgId=1, Palcare mfgId=4).
            if (paramVars.eType === "6") {
                pageContent = isV2
                    ? <WanderingProfilePage id={paramVars.kType} />
                    : <AccutechDevice id={paramVars.kType} />;
            } else {
                pageContent = isV2
                    ? <DeviceProfilePage id={paramVars.kType} mfgId={paramVars.eType} />
                    : (paramVars.eType === "4"
                        ? <PalcareDevice id={paramVars.kType} />
                        : <InovonicsDevice id={paramVars.kType} />);
            }
        } else if (paramVars?.eType) {
            // Legacy: /community/devices/:deviceId (backwards compat, assume Inovonics)
            pageContent = isV2
                ? <DeviceProfilePage id={paramVars.eType} mfgId="1" />
                : <InovonicsDevice id={paramVars.eType} />;
        } else {
            pageContent = isV2 ? <DevicesPage /> : <Devices />;
        }
    }else if (paramVars.dType === "wander_devices") {
        pageContent = isV2 ? <DevicesPage initialTab="wandering" /> : <WanderDevices />;
    }else if (paramVars.dType === "speaker_devices" && iAmHigherAuthority(10, dData?.role_weight) && dData?.campusData?.[dData.acnt?.toString()]?.is_grandstream) {
        pageContent = <DevicesPage initialTab="speakers" />;
    }else if (paramVars.dType === "system_devices" && iAmHigherAuthority(10, dData?.role_weight)) {
        pageContent = isV2 ? <DevicesPage initialTab="system" /> : <SystemDevices />;
    }else if (paramVars.dType === "server_devices" && iAmHigherAuthority(10, dData?.role_weight)) {
        if (paramVars?.eType && iAmHigherAuthority(80, dData?.role_weight)) {
            // Server profile drill-down — restricted to edit-rights users
            pageContent = isV2 ? <ServerProfilePage id={paramVars.eType} /> : <ServerProfile id={paramVars.eType} />;
        } else if (!paramVars?.eType) {
            pageContent = isV2 ? <DevicesPage initialTab="servers" /> : <ServerDevices />;
        } else {
            pageContent = (
                <div>
                    404 Page not found
                </div>
            );
        }
    }else{
        pageContent = (
            <div>
                404 Page not found
            </div>
        );
    }

    return (iAmHigherAuthority(10, dData?.role_weight)) ? (
        <>
            {dData?.migrated && <SessionReload />}
            {pageContent}
        </>
    ) : <Navigate to={"/"}/>;
};

export default Community;